import {
  doc,
  getDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'

// Single document ID for the current policy
const POLICY_DOC_ID = 'currentPolicy'

/**
 * Default leave policy content
 */
const DEFAULT_POLICY = {
  annualLeave: `• 15 days per year
• Accrues at 1.25 days per month
• Maximum carryover: 5 days to next year
• Carried over days must be used within 6 months
• Requires manager approval
• Minimum 3 days notice for planned leave`,

  sickLeave: `• 10 days per year
• Medical certificate required for 3+ consecutive days
• Can be carried forward to next year
• Unused sick leave does not encash on separation
• Notification required on the same day before 10 AM
• Emergency situations are handled case by case`,

  casualLeave: `• 5 days per year
• Minimum 24 hours notice required
• Cannot be combined with other leave types
• Cannot be carried forward to next year
• Subject to manager approval
• Not applicable during notice period`,

  parentalLeave: `• Maternity Leave: 6 months (26 weeks) paid leave
• Paternity Leave: 2 weeks paid leave
• Must apply at least 2 months in advance
• Medical certificate required
• Additional unpaid leave may be granted on request
• Job protection guaranteed during leave period`,

  publicHolidays: `• Public holidays are in addition to annual leave entitlement
• Refer to the Holidays module for the current year's list
• Holiday calendar varies by office location
• Working on public holidays requires prior approval
• Compensatory off granted for working on holidays
• Festival advance available as per company policy`,

  applicationProcess: `• Submit leave request via the Time Off module
• Manager approval required for all leave types
• Standard approval timeline: 48 hours
• Check your leave balance before applying
• Cancellation requests must be submitted immediately
• Emergency leave can be applied retroactively with documentation
• Half-day leave options available for certain leave types`
}

/**
 * Get current leave policy
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const getLeavePolicy = async () => {
  try {
    const docRef = doc(db, 'leavePolicy', POLICY_DOC_ID)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return {
        success: true,
        data: docSnap.data()
      }
    } else {
      // Initialize with default policy if doesn't exist
      const result = await initializeDefaultPolicy()
      return result
    }
  } catch (error) {
    console.error('Get leave policy error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Initialize default leave policy (first-time setup)
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const initializeDefaultPolicy = async () => {
  try {
    const now = Timestamp.now()
    const policyData = {
      ...DEFAULT_POLICY,
      lastUpdatedBy: 'System',
      lastUpdatedAt: now,
      createdAt: new Date().toISOString()
    }

    const docRef = doc(db, 'leavePolicy', POLICY_DOC_ID)
    await setDoc(docRef, policyData)

    return {
      success: true,
      data: policyData
    }
  } catch (error) {
    console.error('Initialize default policy error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update leave policy (admin only)
 * @param {object} data - Policy data to update
 * @param {string} adminName - Name of admin making the update
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateLeavePolicy = async (data, adminName) => {
  try {
    // Validate required fields
    const requiredFields = [
      'annualLeave',
      'sickLeave',
      'casualLeave',
      'parentalLeave',
      'publicHolidays',
      'applicationProcess'
    ]

    for (const field of requiredFields) {
      if (!data[field]) {
        return { success: false, error: `Missing required field: ${field}` }
      }
    }

    // Validate content length (max 5000 chars per section)
    const maxLength = 5000
    for (const field of requiredFields) {
      if (data[field].length > maxLength) {
        return {
          success: false,
          error: `${field} exceeds maximum length of ${maxLength} characters`
        }
      }
    }

    // Update policy
    const now = Timestamp.now()
    const updateData = {
      ...data,
      lastUpdatedBy: adminName || 'Admin',
      lastUpdatedAt: now
    }

    const docRef = doc(db, 'leavePolicy', POLICY_DOC_ID)
    await setDoc(docRef, updateData, { merge: true })

    return { success: true }
  } catch (error) {
    console.error('Update leave policy error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Format Firestore timestamp to readable date
 * @param {Timestamp|Date|string} timestamp - Firestore timestamp, Date object, or ISO string
 * @returns {string} - Formatted date
 */
export const formatPolicyDate = (timestamp) => {
  if (!timestamp) return 'Unknown'

  try {
    let date

    // Handle Firestore Timestamp object
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate()
    }
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp
    }
    // Handle ISO string or timestamp number
    else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp)
    }
    // Unknown format
    else {
      return 'Unknown'
    }

    // Validate date
    if (isNaN(date.getTime())) {
      return 'Unknown'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Format date error:', error)
    return 'Unknown'
  }
}

/**
 * Helper: Get policy section titles
 * @returns {array} - Array of section objects
 */
export const getPolicySections = () => {
  return [
    {
      key: 'annualLeave',
      title: 'Annual Leave',
      icon: 'fas fa-calendar-days'
    },
    {
      key: 'sickLeave',
      title: 'Sick Leave',
      icon: 'fas fa-notes-medical'
    },
    {
      key: 'casualLeave',
      title: 'Casual Leave',
      icon: 'fas fa-calendar-check'
    },
    {
      key: 'parentalLeave',
      title: 'Maternity / Paternity Leave',
      icon: 'fas fa-baby'
    },
    {
      key: 'publicHolidays',
      title: 'Public Holidays',
      icon: 'fas fa-calendar-star'
    },
    {
      key: 'applicationProcess',
      title: 'Leave Application Process',
      icon: 'fas fa-clipboard-check'
    }
  ]
}
